// src/components/analytics/CategoryChart.js - Category breakdown charts
import { useState, useEffect, useRef } from 'react';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const PIE_TO_BAR_THRESHOLD = 6;

const CategoryChart = ({ data }) => {
  const [chartType, setChartType] = useState('pie');
  const defaultBarApplied = useRef(false);
  useEffect(() => {
    if (data && data.length > PIE_TO_BAR_THRESHOLD && !defaultBarApplied.current) {
      setChartType('bar');
      defaultBarApplied.current = true;
    }
  }, [data]);

  if (!data || data.length === 0) {
    return (
      <div className="flex items-center justify-center h-64 text-gray-500 dark:text-shayek-dark-500">
        No category data available
      </div>
    );
  }

  // Format category names: preserve Arabic/non-Latin; only split on _ for Latin snake_case
  const formatCategory = (category) => {
    const raw = String(category ?? '').trim();
    if (!raw) return raw;
    const isLikelyLatin = /^[\x00-\x7F_\s]+$/.test(raw);
    if (!isLikelyLatin || !raw.includes('_')) return raw;
    return raw
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const chartData = data.map(item => ({
    name: formatCategory(item.category),
    value: item.count,
    category: item.category
  }));

  // Color palette matching the Shayek theme
  const COLORS = [
    '#751F19', // Shayek primary red
    '#8b5cf6', // purple
    '#10b981', // green
    '#f59e0b', // amber
    '#ef4444', // red
    '#ec4899', // pink
    '#06b6d4', // cyan
    '#84cc16', // lime
  ];

  const isDark = document.documentElement.classList.contains('dark');
  const gridColor = isDark ? '#333333' : '#e5e7eb';
  const axisColor = isDark ? '#8A8A8A' : '#6b7280';
  const tooltipBg = isDark ? '#2A2A2A' : '#fff';
  const tooltipBorder = isDark ? '#333333' : '#e5e7eb';
  const tooltipText = isDark ? '#E0E0E0' : '#000';

  return (
    <div>
      <div className="flex justify-end mb-4">
        <div className="flex gap-2 bg-gray-100 dark:bg-shayek-dark-200 rounded-lg p-1">
          <button
            onClick={() => setChartType('pie')}
            className={`px-3 py-1 text-sm rounded transition-colors ${
              chartType === 'pie'
                ? 'bg-white dark:bg-shayek-dark-100 text-gray-900 dark:text-shayek-dark-900 shadow-sm'
                : 'text-gray-600 dark:text-shayek-dark-500 hover:text-gray-900 dark:hover:text-shayek-dark-700'
            }`}
          >
            Pie
          </button>
          <button
            onClick={() => setChartType('bar')}
            className={`px-3 py-1 text-sm rounded transition-colors ${
              chartType === 'bar'
                ? 'bg-white dark:bg-shayek-dark-100 text-gray-900 dark:text-shayek-dark-900 shadow-sm'
                : 'text-gray-600 dark:text-shayek-dark-500 hover:text-gray-900 dark:hover:text-shayek-dark-700'
            }`}
          >
            Bar
          </button>
        </div>
      </div>

      <ResponsiveContainer width="100%" height={300}>
        {chartType === 'pie' ? (
          <PieChart>
            <Pie
              data={chartData}
              cx="50%"
              cy="50%"
              outerRadius={100}
              fill="#8884d8"
              dataKey="value"
            >
              {chartData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
              ))}
            </Pie>
            <Tooltip
              formatter={(value, name) => [`${value} (${((value / chartData.reduce((s, d) => s + d.value, 0)) * 100).toFixed(0)}%)`, name]}
              contentStyle={{
                backgroundColor: tooltipBg,
                border: `1px solid ${tooltipBorder}`,
                borderRadius: '8px',
                padding: '8px',
                color: tooltipText
              }}
              labelStyle={{ color: tooltipText }}
            />
            <Legend
              layout="vertical"
              align="right"
              verticalAlign="middle"
              wrapperStyle={{ fontSize: '12px' }}
              formatter={(value) => value}
            />
          </PieChart>
        ) : (
          <BarChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 80 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
            <XAxis
              dataKey="name"
              stroke={axisColor}
              style={{ fontSize: '12px' }}
              angle={-45}
              textAnchor="end"
              height={80}
              tick={{ fill: axisColor }}
            />
            <YAxis
              stroke={axisColor}
              style={{ fontSize: '12px' }}
              tick={{ fill: axisColor }}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: tooltipBg,
                border: `1px solid ${tooltipBorder}`,
                borderRadius: '8px',
                padding: '8px',
                color: tooltipText
              }}
              labelStyle={{ color: tooltipText }}
            />
            <Bar dataKey="value" fill="#751F19" radius={[8, 8, 0, 0]}>
              {chartData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
              ))}
            </Bar>
          </BarChart>
        )}
      </ResponsiveContainer>
    </div>
  );
};

export default CategoryChart;

